import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { messages } from './messages';

/**
 * Document types supported per carrier family.
 * Carriers are matched by prefix of carrierApiCode (e.g. "ups_ground" → "ups").
 */
const FEDEX_TYPES = ['usmca_commercial_invoice_certification_of_origin', 'usmca_certification_of_origin', 'pro_forma_invoice', 'other'];

const UPS_TYPES = [
  'certificate_of_origin',
  'usmca_commercial_invoice_certification_of_origin',
  'usmca_certification_of_origin',
  'other',
  'authorization_form',
  'export_accompanying_document',
  'export_license',
  'import_permit',
  'power_of_attorney',
  'packing_list',
  'sed_document',
  'shippers_letter_of_instruction',
  'declaration',
];

const CARRIER_DOCUMENT_TYPES = {
  fedex: FEDEX_TYPES,
  ups: UPS_TYPES,
};

const CARRIER_FAMILIES = Object.keys(CARRIER_DOCUMENT_TYPES);

/**
 * Resolve carrier family from a carrierApiCode (e.g. "ups_ground" → "ups", "fedex_express_saver" → "fedex").
 */
const resolveCarrierFamily = (carrierApiCode: string | null): string | null => {
  if (!carrierApiCode) return null;
  const code = carrierApiCode.toLowerCase();
  return CARRIER_FAMILIES.find((family) => code.startsWith(family)) ?? null;
};

/**
 * Filter document types to only those supported by the given carrier.
 * If carrier is unknown, returns all types unfiltered.
 */
export const getDocumentTypesForCarrier = (carrierApiCode: string | null, allTypes: string[]) => {
  const family = resolveCarrierFamily(carrierApiCode);
  const supported = family ? (CARRIER_DOCUMENT_TYPES as Record<string, string[]>)[family] : null;
  return supported ? allTypes.filter((type: string) => supported.includes(type)) : allTypes;
};

/**
 * Find document types that are present in uploaded documents but not supported
 * by the current carrier. Returns human-readable labels for display in a warning.
 */
export const getIncompatibleDocumentTypeLabels = (
  intl: IntlShape,
  documents: { documentType: string }[],
  availableDocumentTypes: string[],
) => {
  const incompatibleTypes = [...new Set(documents.map((d) => d.documentType).filter((type) => !availableDocumentTypes.includes(type)))];
  return incompatibleTypes.map((type) => getDocumentTypeLabel(intl, type));
};

/**
 * Internationalized labels for API document type values.
 * Ordered alphabetically for display.
 */
export const documentTypeMessages = defineMessages({
  authorization_form: { description: 'Customs document type label', defaultMessage: 'Authorization Form', id: 'Y54ZNP' },
  certificate_of_origin: { description: 'Customs document type label', defaultMessage: 'Certificate of Origin', id: 'f650/U' },
  commercial_invoice: { description: 'Customs document type label', defaultMessage: 'Commercial Invoice', id: 'J3np5V' },
  declaration: { description: 'Customs document type label', defaultMessage: 'Declaration', id: 'u1V2GA' },
  export_accompanying_document: {
    description: 'Customs document type label',
    defaultMessage: 'Export Accompanying Document',
    id: 'kXTd6+',
  },
  export_license: { description: 'Customs document type label', defaultMessage: 'Export License', id: '39h68+' },
  import_permit: { description: 'Customs document type label', defaultMessage: 'Import Permit', id: 'UMTVCp' },
  one_time_nafta: { description: 'Customs document type label', defaultMessage: 'One-Time NAFTA', id: 'WU3J0m' },
  other: { description: 'Customs document type label', defaultMessage: 'Other', id: 'oupggB' },
  packing_list: { description: 'Customs document type label', defaultMessage: 'Packing List', id: '0AUWgD' },
  power_of_attorney: { description: 'Customs document type label', defaultMessage: 'Power of Attorney', id: 'jIhHjC' },
  pro_forma_invoice: { description: 'Customs document type label', defaultMessage: 'Pro Forma Invoice', id: 'KdEtHf' },
  sed_document: { description: 'Customs document type label', defaultMessage: 'SED Document', id: 'WoNPTl' },
  shippers_letter_of_instruction: {
    description: 'Customs document type label',
    defaultMessage: "Shipper's Letter of Instruction",
    id: 'oSrYYp',
  },
  usmca_certification_of_origin: {
    description: 'Customs document type label',
    defaultMessage: 'USMCA Certification of Origin',
    id: '86Is90',
  },
  usmca_commercial_invoice_certification_of_origin: {
    description: 'Customs document type label',
    defaultMessage: 'USMCA Commercial Invoice Certification of Origin',
    id: 'rKo8fU',
  },
});

/**
 * Get translated document type label.
 */
export const getDocumentTypeLabel = (intl: IntlShape, type: string): string => {
  const msg = documentTypeMessages[type as keyof typeof documentTypeMessages];
  return msg ? intl.formatMessage(msg) : type;
};

type DocumentTypeDropdownProps = {
  selectedType: string | null;
  availableTypes: string[];
  onChange: (type: string) => void;
  isDisabled?: boolean;
  hasDocuments?: boolean;
};

export const DocumentTypeDropdown = ({ selectedType, availableTypes, onChange, isDisabled, hasDocuments }: DocumentTypeDropdownProps) => {
  const intl = useIntl();
  const selectedLabel = selectedType ? getDocumentTypeLabel(intl, selectedType) : null;
  const placeholderMessage = hasDocuments ? messages.documentTypePlaceholderAnother : messages.documentTypePlaceholder;

  return (
    <DropdownMenu disabled={isDisabled} dropdownMenuShouldHaveWidthOfButton fullWidth value={selectedLabel} variant='form'>
      <DropdownMenuToggler>{selectedLabel ?? <FormattedMessage {...placeholderMessage} />}</DropdownMenuToggler>
      {availableTypes.map((type) => (
        <DropdownMenuItem key={type} onClick={() => onChange(type)} selected={selectedType === type}>
          {getDocumentTypeLabel(intl, type)}
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
};
